import { api } from '@/components/Functional/lib/request'
import { logger } from '@/components/Functional/utils/logger'
import {
  MynaviAreasResponseSchema,
  MynaviCollegeDetailResponseSchema,
  MynaviCollegeTypesResponseSchema,
  MynaviCollegesFilterCountResponseSchema,
  MynaviCollegesFilterResponseSchema,
  MynaviCollegesIncrementalResponseSchema,
  MynaviCollegesSearchResponseSchema,
  MynaviEmailCheckResponseSchema,
  MynaviEmailRegisterResponseSchema,
  MynaviEmailResendResponseSchema,
  MynaviEmailVerifyResponseSchema,
  MynaviJobCategoriesResponseSchema,
  MynaviOpenCampusCountResponseSchema,
  MynaviOpenCampusesResponseSchema,
  MynaviPointTransactionsResponseSchema,
  MynaviStudyFieldsResponseSchema,
  MynaviWishToVisitResponseSchema,
} from '@/schemas/v2/mynavi'
import type {
  MynaviAreasResponse,
  MynaviCollegeDetailResponse,
  MynaviCollegeTypesResponse,
  MynaviCollegesFilterCountResponse,
  MynaviCollegesFilterResponse,
  MynaviCollegesIncrementalResponse,
  MynaviCollegesSearchResponse,
  MynaviEmailCheckResponse,
  MynaviEmailRegisterResponse,
  MynaviEmailResendResponse,
  MynaviEmailVerifyResponse,
  MynaviJobCategoriesResponse,
  MynaviOpenCampusCountResponse,
  MynaviOpenCampusesResponse,
  MynaviPointTransactionsResponse,
  MynaviStudyFieldsResponse,
  MynaviWishToVisitResponse,
} from '@/schemas/v2/mynavi'

const BASE = process.env.NEXT_PUBLIC_API_URL ?? ''
const MYNAVI_BASE = `${BASE}/api/v2/mynavi`

type FetchOptions = { headers?: Record<string, string> }

// ─── GET /api/v2/mynavi/areas ──────────────────────────────────────────────

export async function fetchMynaviAreas(all = false, options?: FetchOptions): Promise<MynaviAreasResponse> {
  const url = `${MYNAVI_BASE}/areas?all=${all}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviAreasResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/college-types ─────────────────────────────────────

export async function fetchMynaviCollegeTypes(options?: FetchOptions): Promise<MynaviCollegeTypesResponse> {
  const url = `${MYNAVI_BASE}/college-types`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviCollegeTypesResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/colleges（インクリメンタルサーチ）─────────────────

export async function fetchMynaviCollegesIncremental(
  q: string,
  options?: FetchOptions,
): Promise<MynaviCollegesIncrementalResponse> {
  const url = `${MYNAVI_BASE}/colleges?q=${encodeURIComponent(q)}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviCollegesIncrementalResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/colleges/search ───────────────────────────────────
// TODO(#3215): 削除予定。学校名キーワード検索は /filter の q パラメータに統合済みで、
// この /search エンドポイント（fetchMynaviCollegesSearch）は現在どこからも呼ばれていない。
// createApi.ts の guest/auth 両セクションの登録も含めて削除すること。
export type FetchMynaviCollegesSearchParams = {
  q: string
  page?: number
  limit?: number
  excludePast?: boolean
}

export async function fetchMynaviCollegesSearch(
  params: FetchMynaviCollegesSearchParams,
  options?: FetchOptions,
): Promise<MynaviCollegesSearchResponse> {
  const query = new URLSearchParams({ q: params.q })
  if (params.page !== undefined) query.set('page', String(params.page))
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.excludePast !== undefined) query.set('excludePast', String(params.excludePast))
  const url = `${MYNAVI_BASE}/colleges/search?${query}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviCollegesSearchResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/colleges/filter ───────────────────────────────────

export type FetchMynaviCollegesFilterParams = {
  q?: string
  collegeTypes?: string[]
  area?: string
  prefecture?: string
  prefectures?: string[]
  studyFieldIds?: number[]
  jobCategoryIds?: number[]
  wishedOnly?: boolean
  eventDateFrom?: string
  eventDateTo?: string
  excludePast?: boolean
  page?: number
  limit?: number
}

export async function fetchMynaviCollegesFilter(
  params: FetchMynaviCollegesFilterParams,
  options?: FetchOptions,
): Promise<MynaviCollegesFilterResponse> {
  const query = new URLSearchParams()
  if (params.q && params.q.trim() !== '') query.set('q', params.q.trim())
  params.collegeTypes?.forEach((v) => query.append('collegeTypes[]', v))
  if (params.prefectures && params.prefectures.length > 0) {
    query.set('prefecture', params.prefectures.join(','))
  } else {
    if (params.area) query.set('area', params.area)
    if (params.prefecture) query.set('prefecture', params.prefecture)
  }
  if (params.studyFieldIds && params.studyFieldIds.length > 0) {
    query.set('studyFieldIds', params.studyFieldIds.join(','))
  }
  if (params.jobCategoryIds && params.jobCategoryIds.length > 0) {
    query.set('jobCategoryIds', params.jobCategoryIds.join(','))
  }
  if (params.wishedOnly !== undefined) query.set('wishedOnly', String(params.wishedOnly))
  if (params.eventDateFrom) query.set('eventDateFrom', params.eventDateFrom)
  if (params.eventDateTo) query.set('eventDateTo', params.eventDateTo)
  if (params.excludePast !== undefined) query.set('excludePast', String(params.excludePast))
  if (params.page !== undefined) query.set('page', String(params.page))
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  const url = `${MYNAVI_BASE}/colleges/filter?${query}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviCollegesFilterResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/colleges/filter/count ─────────────────────────────

export async function fetchMynaviCollegesFilterCount(
  params: Omit<FetchMynaviCollegesFilterParams, 'page' | 'limit'>,
  options?: FetchOptions,
): Promise<MynaviCollegesFilterCountResponse> {
  const query = new URLSearchParams()
  if (params.q && params.q.trim() !== '') query.set('q', params.q.trim())
  params.collegeTypes?.forEach((v) => query.append('collegeTypes[]', v))
  if (params.prefectures && params.prefectures.length > 0) {
    query.set('prefecture', params.prefectures.join(','))
  } else {
    if (params.area) query.set('area', params.area)
    if (params.prefecture) query.set('prefecture', params.prefecture)
  }
  if (params.studyFieldIds && params.studyFieldIds.length > 0) {
    query.set('studyFieldIds', params.studyFieldIds.join(','))
  }
  if (params.jobCategoryIds && params.jobCategoryIds.length > 0) {
    query.set('jobCategoryIds', params.jobCategoryIds.join(','))
  }
  if (params.wishedOnly !== undefined) query.set('wishedOnly', String(params.wishedOnly))
  if (params.eventDateFrom) query.set('eventDateFrom', params.eventDateFrom)
  if (params.eventDateTo) query.set('eventDateTo', params.eventDateTo)
  if (params.excludePast !== undefined) query.set('excludePast', String(params.excludePast))
  const url = `${MYNAVI_BASE}/colleges/filter/count?${query}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviCollegesFilterCountResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/colleges/{id} ─────────────────────────────────────

export async function fetchMynaviCollegeDetail(
  id: number,
  excludePast = false,
  options?: FetchOptions,
): Promise<MynaviCollegeDetailResponse> {
  const url = `${MYNAVI_BASE}/colleges/${id}${excludePast ? '?excludePast=true' : ''}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviCollegeDetailResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/study-fields ──────────────────────────────────────

export async function fetchMynaviStudyFields(all = false, options?: FetchOptions): Promise<MynaviStudyFieldsResponse> {
  const url = `${MYNAVI_BASE}/study-fields?all=${all}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviStudyFieldsResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/job-categories ────────────────────────────────────

export async function fetchMynaviJobCategories(
  all = false,
  options?: FetchOptions,
): Promise<MynaviJobCategoriesResponse> {
  const url = `${MYNAVI_BASE}/job-categories?all=${all}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviJobCategoriesResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/open-campuses（認証必須）─────────────────────────

export async function fetchMynaviOpenCampuses(
  lat: number,
  lon: number,
  options?: FetchOptions,
): Promise<MynaviOpenCampusesResponse> {
  const url = `${MYNAVI_BASE}/open-campuses?lat=${lat}&lon=${lon}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviOpenCampusesResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/email/check（認証必須）───────────────────────────

export async function fetchMynaviEmailCheck(options?: FetchOptions): Promise<MynaviEmailCheckResponse> {
  const url = `${MYNAVI_BASE}/email/check`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviEmailCheckResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── POST /api/v2/mynavi/email（認証必須）────────────────────────────────

export async function postMynaviEmail(email: string, options?: FetchOptions): Promise<MynaviEmailRegisterResponse> {
  const url = `${MYNAVI_BASE}/email`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, { email }, options)
    return MynaviEmailRegisterResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── POST /api/v2/mynavi/email/verify（認証必須）─────────────────────────

export async function postMynaviEmailVerify(
  email: string,
  code: string,
  options?: FetchOptions,
): Promise<MynaviEmailVerifyResponse> {
  const url = `${MYNAVI_BASE}/email/verify`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, { email, code }, options)
    return MynaviEmailVerifyResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── POST /api/v2/mynavi/email/resend（認証必須）─────────────────────────

export async function postMynaviEmailResend(email: string, options?: FetchOptions): Promise<MynaviEmailResendResponse> {
  const url = `${MYNAVI_BASE}/email/resend`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, { email }, options)
    return MynaviEmailResendResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── POST /api/v2/mynavi/wish-to-visit（認証必須）────────────────────────

export async function postMynaviWishToVisit(
  openCampusId: number,
  isWished: boolean,
  options?: FetchOptions,
): Promise<MynaviWishToVisitResponse> {
  const url = `${MYNAVI_BASE}/wish-to-visit`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, { openCampusId, isWished }, options)
    return MynaviWishToVisitResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/point-transactions（認証必須）────────────────────

export type FetchMynaviPointTransactionsParams = {
  /** EARN=獲得履歴, EXCHANGE=消費履歴 */
  type: 'EARN' | 'EXCHANGE'
  cursor?: string | null
  limit?: number
}

export async function fetchMynaviPointTransactions(
  params: FetchMynaviPointTransactionsParams,
  options?: FetchOptions,
): Promise<MynaviPointTransactionsResponse> {
  const query = new URLSearchParams({ type: params.type })
  if (params.cursor) query.set('cursor', params.cursor)
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  const url = `${MYNAVI_BASE}/point-transactions?${query}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviPointTransactionsResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/mynavi/open-campuses/count ───────────────────────────────

export async function fetchMynaviOpenCampusCount(
  date: string,
  options?: FetchOptions,
): Promise<MynaviOpenCampusCountResponse> {
  const url = `${MYNAVI_BASE}/open-campuses/count?date=${encodeURIComponent(date)}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviOpenCampusCountResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}
